import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ErrorMessage, ErrorType, BaseResponseCore } from './BaseResponse';
import BaseApiRequestModel from './BaseApiRequestModel';
import SessionHelper from './SessionHelper';
import {
  buildApiQueryKey,
  invalidateApiQueries,
  invalidateApiQueriesByPrefix,
  isApiFailureResponse,
  normalizeQueryParams,
  queryClient,
  QUERY_STALE_TIME_MS,
} from './queryClient';

/** Thrown / mapped when Axios receives a non-2xx HTTP response (e.g. 400). */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly url: string;
  readonly body: unknown;

  constructor(status: number, url: string, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

export type SessionRequest = Partial<Pick<BaseApiRequestModel, 'AccessToken'>>;
export type ApiRequestModel = object & { AccessToken?: string };

export default class BaseApi {
  protected static BaseUrl: string =
    process.env.REACT_APP_API_BASE_URL ||
    (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? 'http://localhost:5039/api/AdminWeb/'
      : 'https://api.modern.roamtravel.koushikisoftware.com/api/AdminWeb/');
  private static reportedClientErrors = new WeakSet<Error>();

  protected static BasePostRequestAsync<T>(
    url: string,
    model: unknown,
  ): Promise<BaseResponseCore<T>> {
    return this.BasePostRequestInternalAsync<T>(url, model, false);
  }

  protected static BasePostRequestWithFileAsync<T>(
    url: string,
    model: unknown,
  ): Promise<BaseResponseCore<T>> {
    return this.BasePostRequestInternalAsync<T>(url, model, true);
  }

  private static InvalidateOnSuccess(
    url: string,
    response: { IsSuccess?: boolean },
  ) {
    if (response?.IsSuccess) {
      // Precision first: invalidate cache entries under this route family.
      const firstSegment = (url || '').split('/')[0];
      if (firstSegment) {
        invalidateApiQueriesByPrefix(`${firstSegment}/`);
      }
      // Safety net for any cross-route dependencies.
      invalidateApiQueries();
    }
  }

  protected static BuildAuthHeaders(model: unknown): Record<string, string> {
    const headers: Record<string, string> = {};
    const requestModel =
      model !== null && typeof model === 'object'
        ? (model as Record<string, unknown>)
        : {};
    const accessToken = requestModel.AccessToken;
    if (accessToken) {
      headers['X-Access-Token'] = String(accessToken);
    }
    if (requestModel.DeviceID || requestModel.DeviceId) {
      headers['X-Device-ID'] = String(requestModel.DeviceID || requestModel.DeviceId);
    }
    if (requestModel.AppVersion) {
      headers['X-App-Version'] = String(requestModel.AppVersion);
    }
    if (typeof window !== 'undefined') {
      const activeTestSessionId =
        sessionStorage.getItem('activeTestSessionId') ||
        localStorage.getItem('activeTestSessionId');
      if (activeTestSessionId) {
        headers['X-Test-Session-Id'] = activeTestSessionId;
      }
    }
    return headers;
  }

  /** Stable serializer improves Cloudflare cache-hit ratio for semantically equal GETs. */
  private static SerializeQueryParams(params: Record<string, unknown>): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(item => {
          search.append(key, String(item));
        });
        return;
      }
      if (typeof value === 'object') {
        search.append(key, JSON.stringify(value));
        return;
      }
      search.append(key, String(value));
    });
    return search.toString();
  }

  private static async ExecuteGetAsync<T>(
    url: string,
    model: unknown,
  ): Promise<BaseResponseCore<T>> {
    const finalApiUrl = this.BaseUrl + url;
    const headers = this.BuildAuthHeaders(model);
    const params = normalizeQueryParams(model);

    try {
      const res = await Axios.get(finalApiUrl, {
        params,
        headers,
        paramsSerializer: p =>
          this.SerializeQueryParams(p as Record<string, unknown>),
      });
      return this.ProcessSuccessAsync<T>(res);
    } catch (error) {
      throw this.ToHttpError(error as AxiosError, finalApiUrl);
    }
  }

  /**
   * GET that always hits the API. Use for reads that also change server state,
   * such as the notification list marking rows delivered.
   */
  protected static async BaseGetRequestFreshAsync<T>(
    url: string,
    model: unknown,
  ): Promise<BaseResponseCore<T>> {
    try {
      return await this.ExecuteGetAsync<T>(url, model);
    } catch (error) {
      if (error instanceof ApiHttpError) {
        return this.FromHttpError<T>(error);
      }
      if (Axios.isAxiosError(error)) {
        return this.FromHttpError<T>(
          this.ToHttpError(error, this.BaseUrl + url),
        );
      }
      throw error;
    }
  }

  /**
   * GET via TanStack Query. Auth in headers only (Cloudflare cache-safe).
   * HTTP errors become BaseResponseCore with IsHttpError=true (not a silent success).
   */
  protected static async BaseGetRequestAsync<T>(
    url: string,
    model: unknown,
  ): Promise<BaseResponseCore<T>> {
    const requestModel = model || {};
    const queryKey = buildApiQueryKey(url, requestModel);

    try {
      return await queryClient.fetchQuery({
        queryKey,
        staleTime: QUERY_STALE_TIME_MS,
        queryFn: async () => {
          const response = await this.ExecuteGetAsync<T>(url, requestModel);
          if (!response.IsSuccess) {
            // Do not cache business failures as successful query data.
            throw response;
          }
          return response;
        },
      });
    } catch (error) {
      if (isApiFailureResponse(error)) {
        return error as BaseResponseCore<T>;
      }
      if (error instanceof ApiHttpError) {
        return this.FromHttpError<T>(error);
      }
      if (Axios.isAxiosError(error)) {
        return this.FromHttpError<T>(
          this.ToHttpError(error, this.BaseUrl + url),
        );
      }
      throw error;
    }
  }

  protected static async BasePostRequestInternalAsync<T>(
    url: string,
    model: unknown,
    isWithFile: boolean,
  ): Promise<BaseResponseCore<T>> {
    const finalApiUrl = this.BaseUrl + url;
    let body: unknown = model;
    let config: AxiosRequestConfig | undefined;

    const authHeaders = this.BuildAuthHeaders(model);

    if (isWithFile) {
      body = this.GetFormDataFromModel(model as Record<string, unknown>);
      config = {
        headers: {
          ...authHeaders,
          'content-type': 'multipart/form-data',
        },
      };
    } else {
      config = { headers: authHeaders };
    }

    try {
      const res = await Axios.post(finalApiUrl, body, config);
      const mapped = this.ProcessSuccessAsync<T>(res);
      this.InvalidateOnSuccess(url, mapped);
      return mapped;
    } catch (error) {
      return this.FromHttpError<T>(
        this.ToHttpError(error as AxiosError, finalApiUrl),
      );
    }
  }

  protected static GetFormDataFromModel(model: Record<string, unknown>) {
    const formData = new FormData();
    Object.keys(model).forEach((k, i) => {
      const d = Object.values(model)[i];
      if (Array.isArray(d)) {
        const da: unknown[] = d;
        da.every(item => {
          const name = k;
          let value: string | Blob = item instanceof Blob ? item : String(item);
          if (!(value instanceof File)) {
            value = JSON.stringify(da);
            formData.append(name, value);
            return false;
          }
          formData.append(name, value);
          return true;
        });
      } else if (d !== undefined && d !== null) {
        formData.append(k, d instanceof Blob ? d : String(d || ''));
      }
    });
    return formData;
  }

  private static ProcessSuccessAsync<T>(res: AxiosResponse): BaseResponseCore<T> {
    const raw = res.data;

    // 1. Direct array response (e.g. List<T> from API)
    if (Array.isArray(raw)) {
      const response = new BaseResponseCore<T>({
        IsSessionExpired: false,
        Data: raw as unknown as T,
        IsSuccess: true,
        Errors: [],
        Message: '',
      });
      response.IsHttpError = false;
      return response;
    }

    // 2. BaseResultModel wrapper with IsSuccess (pure PascalCase, zero backfall)
    if (
      raw &&
      typeof raw === 'object' &&
      'IsSuccess' in raw
    ) {
      const r = raw as BaseResponseCore<T>;
      r.IsSuccess = Boolean((raw as Record<string, unknown>).IsSuccess);
      r.IsHttpError = false;
      return r;
    }

    // 3. Direct object / DTO response without legacy wrapper
    const response = new BaseResponseCore<T>({
      IsSessionExpired: false,
      Data: raw as unknown as T,
      IsSuccess: true,
      Errors: [],
      Message: '',
    });
    response.IsHttpError = false;
    return response;
  }

  private static ToHttpError(error: AxiosError, url: string): ApiHttpError {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    const message = this.BuildHttpErrorMessage(status, url, body, error.message);
    return new ApiHttpError(status, url, message, body);
  }

  private static BuildHttpErrorMessage(
    status: number,
    url: string,
    body: unknown,
    fallbackMessage?: string,
  ): string {
    const textBody = typeof body === 'string' ? body : '';
    const isHtmlError = /<\s*html[\s>]/i.test(textBody);

    if (status === 400) {
      if (isHtmlError) {
        return `Bad request (400). Check API URL and sign in again to refresh your access token.`;
      }
      return `Bad request (400) for ${url}.`;
    }

    if (status === 401) {
      return `Unauthorized (401). Please sign in again.`;
    }

    if (status === 403) {
      return `Forbidden (403). You do not have permission to perform this action.`;
    }

    if (status === 404) {
      return `API route not found (404) for ${url}.`;
    }

    if (status >= 500) {
      return `Server error (${status}). Please try again shortly.`;
    }

    return status > 0
      ? `HTTP ${status} for ${url}`
      : fallbackMessage || `Network error for ${url}`;
  }

  private static FromHttpError<T>(error: ApiHttpError): BaseResponseCore<T> {
    // eslint-disable-next-line no-console
    console.error('[BaseApi]', error.message, error.body);
    if (error.status === 0 || error.status >= 500) {
      const route = this.GetPathOnly(error.url);
      void this.ReportClientError({
        ErrorKind: 'ApiHttpError',
        Message: error.status === 0 ? 'Network request failed.' : error.message,
        Route: route,
        HttpStatus: error.status || undefined,
      });
    }
    const response = new BaseResponseCore<T>({
      IsSessionExpired: false,
      Data: undefined as unknown as T,
      IsSuccess: false,
      Errors: [],
      Message: error.message,
    });
    response.IsHttpError = true;
    response.HttpStatus = error.status;
    response.Errors.push(
      new ErrorMessage({
        AdditionInfo: error.body,
        ErrorType: ErrorType.Error,
        Message: error.message,
      }),
    );
    return response;
  }

  public static ReportClientException(error: Error, stack?: string): void {
    if (this.reportedClientErrors.has(error)) {
      return;
    }
    this.reportedClientErrors.add(error);
    void this.ReportClientError({
      ErrorKind: error.name,
      Message: error.message,
      Stack: stack || error.stack,
    });
  }

  public static async ReportClientError(
    error: {
      ErrorKind?: string;
      Message?: string;
      Stack?: string;
      Route?: string;
      HttpStatus?: number;
    },
  ): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const correlationId = this.CreateClientReference();
    const endpoint = 'https://telemetry.test.roamtravel.koushikisoftware.com/api/logs';
    const appToken = '4a4c7e98-0b17-4f20-94d7-c264a0d6f839';
    try {
      await Axios.post(
        endpoint,
        {
          application: 'Roam_Admin_Panel',
          message: (error.Message || 'Unknown client error').slice(0, 4000),
          level: 'error',
          environment: process.env.NODE_ENV || 'production',
          timestamp: new Date().toISOString(),
          metadata: {
            source: 'AdminWeb',
            errorKind: (error.ErrorKind || 'Error').slice(0, 80),
            stack: error.Stack?.slice(0, 12000),
            route: error.Route || window.location.pathname,
            appVersion: process.env.REACT_APP_VERSION || 'unknown',
            correlationId: correlationId,
            clientReference: this.GetSessionReference(),
            httpStatus: error.HttpStatus,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Log-Token': appToken,
            'X-Correlation-ID': correlationId,
          },
          timeout: 4000,
        },
      );
    } catch (reportingError) {
      // Avoid a reporting loop while keeping telemetry delivery failures visible in diagnostics.
      console.warn('[ErrorTelemetry] Failed to submit client error report.', reportingError);
    }
  }

  private static GetPathOnly(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url.split('?')[0];
    }
  }

  private static GetSessionReference(): string {
    const key = 'error_telemetry_session';
    try {
      let reference = sessionStorage.getItem(key);
      if (!reference) {
        reference = this.CreateClientReference();
        sessionStorage.setItem(key, reference);
      }
      return reference;
    } catch {
      return this.CreateClientReference();
    }
  }

  private static CreateClientReference(): string {
    const browserCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    if (browserCrypto && typeof browserCrypto.getRandomValues === 'function') {
      const buffer = new Uint32Array(4);
      browserCrypto.getRandomValues(buffer);
      return Array.from(buffer, value => value.toString(16).padStart(8, '0')).join('-');
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  public static GetBaseRequestModel(): BaseApiRequestModel {
    const model = new BaseApiRequestModel();
    const obj = SessionHelper.GetSession();
    if (obj) {
      model.AccessToken = obj.AccessToken;
    }
    return model;
  }
}
